import { AuthService } from '@/auth/service/auth.service';
import { RequirementTypes } from '@/enums/license-request';
import { CompleteLicense, CompleteLicenseWithDate } from '@/interfaces/dashboard/licenses';
import { AssetLicenseValidation, Contracts } from '@/interfaces/new-license-request/new-license-request-interfaces';
import {
  CreateRequirement, Details, ExtendRequirementExist, Flow, FlowFromBack, Manager,
  NewLicenseRequirementExist, RenewRequirementExist, RequestDocuments, RequiredDocument
} from '@/interfaces/requirement';
import { StoreState } from '@/store';
import { CacheService } from '@/utils/cacheService/cache.service';
import { parseISOString } from '@/utils/parseISOString';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { exhaustMap, first, map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class RequirementService {

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    private store: Store<StoreState>,
    private cacheService: CacheService,
  ) { }

  getAllManagers(assetId: number): Observable<Manager[]> {
    const source$ = this.http.get<Manager[]>(`/Requirement/RequestManagers?assetId=${assetId}`);
    return this.cacheService.save<Manager[]>(`RequestManagers${assetId}`, 24 * 60, source$);
  }

  requestNewId(requirementType: RequirementTypes = RequirementTypes['Nueva Licencia'], licenceId?: number): Observable<number> {
    const url = `/Requirement/GetNewId?requirementTypeId=${requirementType}&licenseId=${licenceId || '0'}`;
    return this.http.get<{ requirementId: number }>(url)
      .pipe(
        map(({ requirementId }) => requirementId),
        take(1),
      );
  }

  getFlow(id: number): Observable<Flow[]> {
    return this.http.get<FlowFromBack[]>(`/Flow/FlowSteps?requirementId=${id}`)
      .pipe(
        map<FlowFromBack[], Flow[]>((flowFromBack) => flowFromBack.map((flowStep, index) => {
          let parsedDate;
          try {
            parsedDate = parseISOString(flowStep.stepDate);
            const limitValidDate = new Date(2000, 0, 1);
            if (parsedDate.getTime() < limitValidDate.getTime()) {
              parsedDate = undefined;
            }
          }
          catch { }

          let approved: Flow['approved'] = 'Pendiente';
          if (parsedDate || flowStep.approved === true) {
            approved = 'Aprobado';
          }
          if (flowStep.approved === false || ['Eliminado', 'Rechazada'].includes(flowStep.step)) {
            approved = 'Rechazado';
          }
          if (flowStep.approved === null) {
            approved = 'Pendiente';
          }

          return ({
            id: index,
            name: flowStep.step,
            approved,
            assigned: flowStep.assigned,
            delegator: flowStep.delegator,
            roleName: flowStep.roleName,
            date: parsedDate,
            roleId: flowStep.roleId,
          });
        })),
      );
  }

  getFirstPendingFlowStep(flow: Flow[]): Flow | undefined {
    return flow.find(({ date }) => !date);
  }

  getDetails(id: number): Observable<Details> {
    return this.getDetailsAll(id).pipe(
      map((details) => {
        let { documents } = details;

        documents = documents.filter(({ isMineRequired }) => !isMineRequired);

        return <Details><unknown>{ ...details, documents };
      }),
    );
  }

  getDetailsAll(id: number): Observable<Details> {
    const source$ = this.http.get<Details>(`Requirement/Details?requirementId=${id}`)
      .pipe(
        map<any, Details>((details: any) => {
          let { documents, equipments } = details;
          documents = documents.map((document: any) => {
            let out = document;
            if (document.dateEmit) {
              out = { ...out, dateEmit: parseISOString(out.dateEmit) };
            }
            if (document.dateFrom) {
              out = { ...out, dateFrom: parseISOString(out.dateFrom) };
            }
            if (document.dateUntil) {
              out = { ...out, dateUntil: parseISOString(out.dateUntil) };
            }

            out.isMineRequired = document.documentRequirementUseTypeId;
            delete out.documentRequirementUseTypeId;

            return out;
          });

          equipments = equipments.map((equipment: any) => {
            if (equipment.expiredAt) {
              const expiredAt = parseISOString(equipment.expiredAt);
              return { ...equipment, expiredAt };
            }
            return equipment;
          });

          return <Details><unknown>{ ...details, documents, equipments };
        }),
        first(),
      );

    return this.cacheService.save<Details>(`getDetailsAll_${id}`, 0.3, source$);
  }

  getDetailsMine(id: number): Observable<Details> {
    return this.getDetailsAll(id).pipe(
      map((details) => {
        // eslint-disable-next-line prefer-const
        let { documents } = details;

        documents = documents.filter(({ isMineRequired }) => isMineRequired);

        return <Details><unknown>{ ...details, documents };
      }),
    );
  }

  getManyRequiredDocuments(requestDocument: RequestDocuments[]): Observable<RequiredDocument[]> {
    if (requestDocument.length === 0) {
      return of([]);
    }
    return this.http.post<RequiredDocument[]>('/Requirement/RequiredDocumentsManyEquipment', requestDocument.map((a) => ({ ...a, equipmentId: a.vehicleTypeId || a.equipmentId })));
  }

  createRequirement(requirement: CreateRequirement, requirementId: number) {
    let { documents } = requirement;
    const role = this.auth.getRoles()[0];

    documents = <any>documents.map((document) => ({
      documentRequiredTypeId: document.documentRequiredTypeId,
      requirementId: document.requirementId,
      fileName: document.fileName,
      id: +document.path,
      dateFrom: document.createFrom,
      dateTo: document.createTo,
      createdAt: document.createAt,
    }));

    return this.http.post(`Requirement/UpdateCreated/${requirementId}`, {
      ...requirement,
      documents,
      requestorRolName: role.name,
      requestorRolId: role.id,
    });
  }

  getLicenseById(id: number): Observable<CompleteLicenseWithDate> {
    return this.http.get<CompleteLicense>(`/License/GetById?licenseId=${id}`).pipe(
      map((license) => ({
        ...license,
        equipments: license.equipments.map((equipment: any) => {
          if (equipment.expiredAt) {
            const expiredAt = parseISOString(equipment.expiredAt);
            return { ...equipment, expiredAt };
          }
          return equipment;
        }),
        documents: license.documents.map((document) => {
          const { dateEmit, dateFrom, dateUntil, documentRequirementUseTypeId, ...rest } = document;
          const out: CompleteLicenseWithDate['documents'][0] = { ...rest, isMineRequired: documentRequirementUseTypeId };
          if (dateEmit) {
            out.dateEmit = parseISOString(dateEmit);
          }
          if (dateFrom) {
            out.dateFrom = parseISOString(dateFrom);
          }
          if (dateUntil) {
            out.dateUntil = parseISOString(dateUntil);
          }

          return out;
        }),
      }
      )),
    );
  }
  getDeletedLicenseById(id: number): Observable<CompleteLicenseWithDate> {
    return this.http.get<CompleteLicense>(`/License/GetDeletedById?licenseId=${id}`).pipe(
      map((license) => ({
        ...license,
        equipments: license.equipments.map((equipment: any) => {
          if (equipment.expiredAt) {
            const expiredAt = parseISOString(equipment.expiredAt);
            return { ...equipment, expiredAt };
          }
          return {
            ...equipment,
            equipmentStatus: equipment.equipmentStatus !== 'Eliminadas' ? equipment.equipmentStatus : 'Eliminado',
          };
        }),
        documents: license.documents.map((document) => {
          const { dateEmit, dateFrom, dateUntil, documentRequirementUseTypeId, ...rest } = document;
          const out: CompleteLicenseWithDate['documents'][0] = { ...rest, isMineRequired: documentRequirementUseTypeId };
          if (dateEmit) {
            out.dateEmit = parseISOString(dateEmit);
          }
          if (dateFrom) {
            out.dateFrom = parseISOString(dateFrom);
          }
          if (dateUntil) {
            out.dateUntil = parseISOString(dateUntil);
          }

          return out;
        }),
      }
      )),
    );
  }

  getLicenseQr(id: number): Observable<Blob> {
    const source$ = this.http.get(`/Document/GetQrCode?licenseId=${id}`, {
      responseType: 'blob',
    });
    // The link is permantent, even if the license is deleted, so the cache is for 1 day or until the
    // user reload the tab
    return this.cacheService.save<Blob>(`GetQrCode${id}`, 24 * 60, source$);
  }

  getContractByAsset(assetId: number, mdmid: string): Observable<Contracts[]> {
    return this.store.select('login').pipe(
      exhaustMap(
        (user) => this.http.get<Contracts[]>(`/Requirement/ContractsByAsset?assetId=${assetId}&rutDv=${mdmid}&mdmidAc=${user.mdmid}`),
      ),
    );
  }

  validateExistingLicense(mdmid: string, assetId: number): Observable<boolean> {
    return this.http.get<AssetLicenseValidation>(`/Requirement/ExistActiveLicenseByQuery?userRutDv=${mdmid}&assetId=${assetId}`).pipe(map((x) => x.exist));
  }

  validateExistingInfraction(mdmid: string, assetId: number): Observable<boolean> {
    return this.http.get<boolean>(`/Infringement/ValidateExistsInfractionByDriverAndAsset?driverMdmid=${mdmid}&assetId=${assetId}`);
  }

  validateExistingNewLicenseRequeriment(params: NewLicenseRequirementExist): Observable<boolean> {
    return this.http.get<AssetLicenseValidation>(
      `/Requirement/ExistProcessRequirement?mdmid=${params.mdmid}&assetId=${params.assetId}&requirementType=${params.requirementType}`,
    ).pipe(map((x) => x.exist));
  }

  validateExistingRenewRequeriment(params: RenewRequirementExist): Observable<AssetLicenseValidation> {
    return this.http.get<AssetLicenseValidation>(
      `/Requirement/ExistProcessRequirement?mdmid=${params.mdmid}&assetId=${params.assetId}&requirementType=${params.requirementType}`,
    );
  }

  validateExistingExtendRequeriment(params: ExtendRequirementExist): Observable<AssetLicenseValidation> {
    return this.http.get<AssetLicenseValidation>(
      `/Requirement/ExistProcessRequirement?mdmid=${params.mdmid}&assetId=${params.assetId}&requirementType=${params.requirementType}`,
    );
  }
}
